import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert, Chip } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import {
  getPublicProgram, getPublicProgramSeason, listPublicProgramSeasons, listPublicDivisions, listPublicProgramGames,
  listPublicDivisionTeams, listPublicProgramMatchups,
} from '../../services/publicProgramService';
import { hasProgramManagementAuthority } from '../../services/programService';
import { divisionSecondaryLabel, competitionTypeLabel, competitionTypeChipStyle } from '../../utils/divisionDisplay';
import { deriveGameResult, formatGameOpponentLine, formatGameScoreLine, formatGameDateCompact } from '../../utils/gameResult';

// Phase C.3 — how many rows the compact Program Upcoming / Latest
// Results sections show at most. Deliberately small and client-side
// only (list_public_program_games, 041, returns every public game for
// the Season; this is display-only capping, not pagination) — this is a
// compact roll-up, not a full cross-Team calendar.
const PROGRAM_GAMES_LIMIT = 5;

// Phase C.2 — the public Program Home (`/programs/:programSlug`, and the
// season-aware `/programs/:programSlug/seasons/:seasonYear` variant —
// same component, `seasonYear` just selects which Season's data loads).
// Fully public: no RequireAuth anywhere in this file. This is the FIRST
// public destination this app has ever had that reads real Program/
// Season/Division data — every read below goes through migration 038's
// public-safe RPCs (publicProgramService.js), never a direct table
// query, so it can never expose a field those RPCs didn't already decide
// was safe.

// Phase C.3 — Upcoming/Latest Results are now real sections (below);
// Development/Program News remain future-module placeholders, no
// fabricated content.
const FUTURE_SECTIONS = [
  { key: 'development', title: 'Development', body: 'Development highlights will appear here as they’re shared.' },
  { key: 'news', title: 'Program News', body: 'Announcements will appear here once the program starts posting.' },
];

/** One compact line: "6th Grade · Blackhawks vs Jefferson · Aug 28"
 * (upcoming) or "...vs Jefferson · W 24–14" (result). Team name links to
 * its public page — every row here already came from list_public_
 * program_games() (041), which only ever returns public-enabled Teams,
 * so a link here can never be dead.
 *
 * Phase C.3.1 — the Division prefix (teamDivisionNames[team_id]) is
 * shown here specifically because this list spans EVERY public Team
 * across the whole Program: Fort Youth Tackle Football's real travel
 * Divisions (6th/7th/8th) all name their Team "Blackhawks" (the school
 * mascot) — team_name alone is genuinely ambiguous on this one surface.
 * This does not change any Team identity/FK — division_name is a purely
 * client-side lookup built from the same public Division/Team RPCs the
 * page already calls (see the Division-context-map effect below), never
 * a new backend projection. Falls back to the bare Team name if the
 * lookup has nothing (e.g. still loading, or an edge case with no
 * matching Division row) rather than showing a blank prefix.
 *
 * Phase C.4 — an internal matchup (game.kind === 'matchup', from
 * list_public_program_matchups(), 042) renders as a neutral "Team A vs
 * Team B" line instead — never clickable: unlike an external game (only
 * ever surfaced for a Team with public_page_enabled = true), an internal
 * matchup's own participating Teams may intentionally have NO public
 * page of their own (the Division is often the public destination for
 * in-house football) — that RPC never returns public_page_enabled, so a
 * link here could point at an unpublished Team's page. Plain text
 * avoids ever risking a dead-feeling link, same principle
 * list_public_division_teams() (038) already established. */
function ProgramGameLine({ game, programSlug, navigate, variant, teamDivisionNames }) {
  const isResult = variant === 'result';

  if (game.kind === 'matchup') {
    const aName = teamDivisionNames[game.team_a_id] ? `${teamDivisionNames[game.team_a_id]} · ${game.team_a_name}` : game.team_a_name;
    const bName = teamDivisionNames[game.team_b_id] ? `${teamDivisionNames[game.team_b_id]} · ${game.team_b_name}` : game.team_b_name;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.75 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
          {aName} vs {bName}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: isResult ? 800 : 400, color: isResult ? '#9eb8d0' : '#6a8aa8', whiteSpace: 'nowrap' }}>
          {isResult ? `${game.team_a_score}–${game.team_b_score}` : formatGameDateCompact(game.event_date)}
        </Typography>
      </Box>
    );
  }

  const result = isResult ? deriveGameResult(game.team_score, game.opponent_score) : null;
  const divisionName = teamDivisionNames[game.team_id];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.75 }}>
      <Typography sx={{ fontSize: '0.8rem' }}>
        {divisionName && (
          <Typography component="span" sx={{ color: '#4a6080' }}>{divisionName} · </Typography>
        )}
        <Typography
          component="span"
          onClick={() => navigate(`/programs/${programSlug}/teams/${game.team_id}`)}
          sx={{ fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {game.team_name}
        </Typography>
        <Typography component="span" sx={{ color: '#6a8aa8' }}> {formatGameOpponentLine(game)}</Typography>
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: isResult ? 800 : 400, color: isResult ? (result === 'W' ? '#2ea84b' : '#f44336') : '#6a8aa8', whiteSpace: 'nowrap' }}>
        {isResult ? formatGameScoreLine(game) : formatGameDateCompact(game.event_date)}
      </Typography>
    </Box>
  );
}

function ProgramGamesSection({ title, games, emptyText, programSlug, navigate, variant, teamDivisionNames }) {
  return (
    <Box sx={{ backgroundColor: 'rgba(8,16,28,0.5)', border: '1px solid #16202e', borderRadius: 2, p: 1.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#9eb8d0', mb: 0.5 }}>{title}</Typography>
      {games.length === 0 ? (
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', lineHeight: 1.4 }}>{emptyText}</Typography>
      ) : (
        <Box>
          {games.map(g => (
            <ProgramGameLine key={g.id} game={g} programSlug={programSlug} navigate={navigate} variant={variant} teamDivisionNames={teamDivisionNames} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function DivisionCard({ division, programSlug, navigate }) {
  // Phase C.3.1 — the secondary line only renders when it's actually new
  // information beyond the Division's own name (divisionSecondaryLabel()
  // returns null for the exact "6th Grade" / "6th" redundancy real data
  // exposed) — never an empty line reserving dead space.
  const secondary = divisionSecondaryLabel(division);
  return (
    <Card
      onClick={() => navigate(`/programs/${programSlug}/divisions/${division.id}`)}
      sx={{
        cursor: 'pointer', backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', height: '100%',
        transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(46,168,75,0.4)' },
      }}
    >
      <CardContent sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 800 }}>{division.name}</Typography>
          <Chip size="small" label={competitionTypeLabel(division.competition_type)} sx={{ fontWeight: 700, ...competitionTypeChipStyle(division.competition_type) }} />
        </Box>
        {secondary && (
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mt: 0.5 }}>{secondary}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

function FutureSection({ section }) {
  return (
    <Box sx={{ backgroundColor: 'rgba(8,16,28,0.5)', border: '1px solid #16202e', borderRadius: 2, p: 1.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#9eb8d0', mb: 0.5 }}>{section.title}</Typography>
      <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', lineHeight: 1.4 }}>{section.body}</Typography>
    </Box>
  );
}

export default function PublicProgramPage() {
  const { programSlug, seasonYear } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [program, setProgram] = useState(null);
  const [season, setSeason] = useState(null);
  const [allSeasons, setAllSeasons] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [games, setGames] = useState([]);
  // Phase C.3.1 — { [team_id]: division_name }, built client-side from
  // the already-public listPublicDivisionTeams() calls below — see
  // ProgramGameLine's own header for why this exists (real Divisions can
  // share a Team name, e.g. every Fort Youth travel Team is "Blackhawks").
  const [teamDivisionNames, setTeamDivisionNames] = useState({});
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);

    getPublicProgram(programSlug).then(async (p) => {
      if (cancelled) return;
      if (!p) {
        setProgram(null);
        setNotFound(true);
        setLoading(false);
        return;
      }
      setProgram(p);

      const yearNum = seasonYear ? Number(seasonYear) : null;
      const [resolvedSeason, seasons] = await Promise.all([
        getPublicProgramSeason(p.id, Number.isFinite(yearNum) ? yearNum : null),
        listPublicProgramSeasons(p.id),
      ]);
      if (cancelled) return;
      setSeason(resolvedSeason);
      setAllSeasons(seasons);

      if (resolvedSeason) {
        const [divs, seasonGames, seasonMatchups] = await Promise.all([
          listPublicDivisions(resolvedSeason.id),
          listPublicProgramGames(p.id, resolvedSeason.id).catch(() => []),
          listPublicProgramMatchups(p.id, resolvedSeason.id).catch(() => []),
        ]);
        if (!cancelled) {
          // Phase C.4 — merged once here, tagged by kind; each internal
          // matchup is exactly one row (list_public_program_matchups
          // queries team_matchups directly, never a per-Team union), so
          // it contributes exactly once below — never duplicated.
          setGames([
            ...seasonGames.map(g => ({ ...g, kind: 'event' })),
            ...seasonMatchups.map(m => ({ ...m, kind: 'matchup' })),
          ]);
          setDivisions(divs);
        }
      } else {
        setDivisions([]);
        setGames([]);
      }
      setLoading(false);
    }).catch(e => {
      if (!cancelled) { setError(e.message); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [programSlug, seasonYear]);

  // Phase C.3.1 — best-effort Division-context lookup for the Upcoming/
  // Latest Results rows above (see ProgramGameLine's own header). One
  // listPublicDivisionTeams() call per Division already on this page
  // (typically a handful) — never a new RPC, never blocks the primary
  // load, and a failure here just means the Team-name-only fallback
  // renders instead of breaking the page.
  useEffect(() => {
    if (divisions.length === 0) { setTeamDivisionNames({}); return; }
    let cancelled = false;
    Promise.all(divisions.map(d => listPublicDivisionTeams(d.id).then(teams => ({ division: d, teams })).catch(() => ({ division: d, teams: [] }))))
      .then(results => {
        if (cancelled) return;
        const map = {};
        results.forEach(({ division, teams }) => {
          teams.forEach(t => { map[t.id] = division.name; });
        });
        setTeamDivisionNames(map);
      });
    return () => { cancelled = true; };
  }, [divisions]);

  // "Manage Program" is the ONLY authenticated-aware thing on this
  // otherwise fully public page — gated on the SAME has_program_
  // management_authority() RPC every existing admin page already uses
  // (025), only ever called when a session exists (that RPC is granted
  // to `authenticated` only — see programService.js's own comment).
  // Logged-out visitors and logged-in non-admins both simply never see
  // this link; neither gains any actual management ability from this
  // page either way (it only ever navigates into the existing, already-
  // authorization-checked /my-programs admin pages).
  useEffect(() => {
    if (!currentUser || !program) { setCanManage(false); return; }
    let cancelled = false;
    hasProgramManagementAuthority(program.id).then(v => { if (!cancelled) setCanManage(v); }).catch(() => { if (!cancelled) setCanManage(false); });
    return () => { cancelled = true; };
  }, [currentUser, program]);

  const otherSeasons = allSeasons.filter(s => s.id !== season?.id);

  // Phase C.3 — compact roll-ups only, no standings: soonest-first
  // Upcoming, most-recent-first Latest Results, each capped at
  // PROGRAM_GAMES_LIMIT. Cancelled games are never shown here (they add
  // no useful information at a cross-Team glance) — the owning Team's
  // own public page still shows them clearly, per that page's own
  // Schedule section.
  const programUpcoming = games.filter(g => g.status === 'scheduled').slice(0, PROGRAM_GAMES_LIMIT);
  const programResults = [...games.filter(g => g.status === 'completed')].reverse().slice(0, PROGRAM_GAMES_LIMIT);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Navbar />
        <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Navbar />
        <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center', flex: 1 }}>
          <Typography sx={{ fontSize: '2.2rem', mb: 1.5 }}>🏟️</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Program not found</Typography>
          <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
            This program doesn't exist or isn't public yet.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate('/programs')} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Browse Programs
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Navbar />
        <Container maxWidth="sm" sx={{ py: 8 }}><Alert severity="error">{error}</Alert></Container>
      </Box>
    );
  }

  const location = [program.city, program.state_code].filter(Boolean).join(', ');

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      {/* Phase C.3.1 — widened from "md" to "lg": real Division counts (up
          to ~6 for a full-size Program) and real game data in Upcoming/
          Latest Results were visibly cramped at "md" during browser
          testing. Every grid below still stacks to a single column on
          narrow/mobile widths regardless of this container's max — this
          only changes how much room content gets on tablet/desktop. */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, flex: 1 }}>
        {/* ── Program header ────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900 }}>{program.name}</Typography>
            {location && <Typography sx={{ color: '#9eb8d0', mt: 0.25 }}>{location}</Typography>}
          </Box>
          {canManage && (
            <Button
              size="small" variant="outlined"
              onClick={() => navigate(`/my-programs/${program.id}/manage`)}
              sx={{ textTransform: 'none', fontWeight: 700, color: '#9eb8d0', borderColor: '#2a3a4e' }}
            >
              Manage Program
            </Button>
          )}
        </Box>

        {/* ── Season identity ───────────────────────────────────────── */}
        <Box sx={{ mb: 3 }}>
          {season ? (
            <>
              <Typography sx={{ fontWeight: 700, color: '#2ea84b' }}>
                {season.name} {season.status !== 'active' && `(${season.status === 'upcoming' ? 'Upcoming' : 'Archived'})`}
              </Typography>
              {otherSeasons.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                  <Typography sx={{ color: '#4a6080', fontSize: '0.78rem' }}>Other seasons:</Typography>
                  {otherSeasons.map(s => (
                    <Typography
                      key={s.id} component="span"
                      onClick={() => navigate(`/programs/${programSlug}/seasons/${s.year}`)}
                      sx={{ color: '#6a8aa8', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: '#9eb8d0' } }}
                    >
                      {s.year}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.9rem' }}>
              This program hasn't published a season yet.
            </Typography>
          )}
        </Box>

        {/* ── Divisions ──────────────────────────────────────────────── */}
        {season && (
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.04em', color: '#e8f0f8', mb: 1.5 }}>
              DIVISIONS
            </Typography>
            {divisions.length === 0 ? (
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>
                Divisions will appear here when the program setup is complete.
              </Typography>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 1.5,
              }}>
                {divisions.map(d => <DivisionCard key={d.id} division={d} programSlug={programSlug} navigate={navigate} />)}
              </Box>
            )}
          </Box>
        )}

        {/* Phase C.3.1 — Upcoming/Latest Results are real, dynamic content
            modules now (real Team/opponent/date/score rows), not tiny
            placeholder cards — real data during browser testing showed
            them visibly squeezed sharing a 4-up row with two empty future
            placeholders. Split into two half-width-at-desktop rows
            instead: Upcoming/Latest Results get real room to breathe,
            Development/Program News (still pure placeholders) form their
            own row below. Every row still stacks to one column on
            mobile. */}
        {season && (
          <>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.5,
              mb: 1.5,
            }}>
              <ProgramGamesSection
                title="Upcoming" games={programUpcoming} programSlug={programSlug} navigate={navigate} variant="upcoming"
                emptyText="Games will appear here once teams publish their schedules." teamDivisionNames={teamDivisionNames}
              />
              <ProgramGamesSection
                title="Latest Results" games={programResults} programSlug={programSlug} navigate={navigate} variant="result"
                emptyText="Results will appear here once games are completed." teamDivisionNames={teamDivisionNames}
              />
            </Box>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.25,
            }}>
              {FUTURE_SECTIONS.map(s => <FutureSection key={s.key} section={s} />)}
            </Box>
          </>
        )}
      </Container>
      <Footer />
    </Box>
  );
}
